import { Capacitor } from '@capacitor/core';
import { supabase } from '../lib/db';

/**
 * Registers the device push token (FCM for web, Capacitor Push for native)
 * and stores it in the `push_tokens` table. The token is used by backend
 * (edge function / cron) to send server-initiated push notifications.
 */
export class PushTokenService {
  private static instance: PushTokenService;
  private currentToken: string | null = null;
  private userId: string | null = null;
  private foregroundUnsub: (() => void) | null = null;

  private constructor() {}

  static getInstance(): PushTokenService {
    if (!PushTokenService.instance) {
      PushTokenService.instance = new PushTokenService();
    }
    return PushTokenService.instance;
  }

  /**
   * Initialise push for the given user.
   * - Web: registers Firebase service worker, gets FCM token.
   * - Native: uses @capacitor/push-notifications to get device token.
   * Stores the token in `push_tokens` so the server can send pushes later.
   */
  async register(userId: string): Promise<void> {
    this.userId = userId;

    try {
      if (Capacitor.isNativePlatform()) {
        await this.registerNative(userId);
      } else {
        await this.registerWeb(userId);
      }
    } catch (error) {
      console.error('[PushToken] Error during registration:', error);
    }
  }

  // ── Web (Firebase Cloud Messaging) ──────────────────────────────
  private async registerWeb(userId: string): Promise<void> {
    // Dynamic import so firebase is tree-shaken on native builds
    const { requestFCMToken, onForegroundMessage } = await import('../lib/firebase');
    const { unifiedNotificationService } = await import('./UnifiedNotificationService');

    const token = await requestFCMToken();
    if (!token) {
      console.warn('[PushToken] Could not obtain FCM token');
      return;
    }

    this.currentToken = token;
    await this.upsertToken(userId, token, 'web');
    console.log('[PushToken] Web push token stored');

    // Handle messages that arrive while the tab IS in focus
    // (background messages are handled by the service worker)
    this.foregroundUnsub = onForegroundMessage((payload) => {
      const data = payload.data || {};
      const notification = payload.notification || {};

      unifiedNotificationService.showNotification({
        title: notification.title || data.title || 'New Notification',
        body: notification.body || data.message || '',
        type: data.entity_type || 'default',
        deepLink: data.deep_link,
        metadata: data,
      });
    });
  }

  // ── Native (Capacitor Push Notifications) ───────────────────────
  private async registerNative(userId: string): Promise<void> {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // Request permission
    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== 'granted') {
      console.warn('[PushToken] Native push permission not granted');
      return;
    }

    // Register with APNs / FCM
    await PushNotifications.register();

    // Listen for the registration token
    PushNotifications.addListener('registration', async (registration) => {
      console.log('[PushToken] Native push token received');
      this.currentToken = registration.value;
      const platform = Capacitor.getPlatform() as 'android' | 'ios';
      await this.upsertToken(userId, registration.value, platform);
    });

    PushNotifications.addListener('registrationError', (error) => {
      console.error('[PushToken] Native push registration error:', error);
    });

    // Handle received notifications when app is in foreground
    PushNotifications.addListener('pushNotificationReceived', async (notification) => {
      console.log('[PushToken] Native foreground push:', notification);
      const { unifiedNotificationService } = await import('./UnifiedNotificationService');
      unifiedNotificationService.showNotification({
        title: notification.title || 'New Notification',
        body: notification.body || '',
        type: (notification.data as any)?.entity_type || 'default',
        deepLink: (notification.data as any)?.deep_link,
        metadata: notification.data as Record<string, any>,
      });
    });

    // Handle notification tap
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification.data as Record<string, any> | undefined;
      if (data?.deep_link) {
        window.dispatchEvent(new CustomEvent('app-navigate', { detail: { path: data.deep_link } }));
      }
    });
  }

  // ── Token persistence ───────────────────────────────────────────
  private async upsertToken(userId: string, token: string, platform: 'web' | 'android' | 'ios'): Promise<void> {
    const { error } = await supabase
      .from('push_tokens')
      .upsert(
        { user_id: userId, token, platform, updated_at: new Date().toISOString() },
        { onConflict: 'token' }
      );

    if (error) {
      console.error('[PushToken] Error storing token:', error);
    }
  }

  async unregister(): Promise<void> {
    if (this.currentToken && this.userId) {
      await supabase
        .from('push_tokens')
        .delete()
        .eq('token', this.currentToken);
    }

    if (this.foregroundUnsub) {
      this.foregroundUnsub();
      this.foregroundUnsub = null;
    }

    if (Capacitor.isNativePlatform()) {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        await PushNotifications.removeAllListeners();
      } catch {}
    }

    this.currentToken = null;
    this.userId = null;
    console.log('[PushToken] Unregistered');
  }
}

export const pushTokenService = PushTokenService.getInstance();
